import type {
  BetMove,
  LoveWinsAllAction,
} from "@cloudnest/love-wins-all-engine";
import { isPlayerId } from "@cloudnest/love-wins-all-engine";

/**
 * Typed replay-event adapter for 러브 윈즈 올 (love-wins-all).
 *
 * Hidden-information game: the persisted `init` event carries the full
 * authoritative state (deck + both hands); the replay runner masks it for
 * display. The only player event is `Bet` (cards are dealt by the engine).
 * Malformed events fail loudly (unknown type / playerId / move).
 */
export type LoveWinsAllReplayEventType = "init" | "Bet";

export const LOVE_WINS_ALL_REPLAY_EVENT_TYPES = [
  "init",
  "Bet",
] as const satisfies readonly LoveWinsAllReplayEventType[];

const BET_MOVES: readonly BetMove[] = ["check", "bet", "call", "raise", "fold"];

export function normalizeLoveWinsAllReplayEventType(
  eventType: string,
): LoveWinsAllReplayEventType {
  if (
    (LOVE_WINS_ALL_REPLAY_EVENT_TYPES as readonly string[]).includes(eventType)
  ) {
    return eventType as LoveWinsAllReplayEventType;
  }
  throw new Error(`Unsupported love-wins-all event type: ${eventType}`);
}

function asPlayerId(value: unknown) {
  if (isPlayerId(value)) return value;
  throw new Error(`Invalid love-wins-all playerId in event payload: ${value}`);
}

function asBetMove(value: unknown): BetMove {
  if (typeof value === "string" && (BET_MOVES as string[]).includes(value)) {
    return value as BetMove;
  }
  throw new Error(`Invalid love-wins-all bet move in event payload: ${value}`);
}

export function loveWinsAllEventToAction(
  eventType: string,
  payload: Record<string, unknown>,
): LoveWinsAllAction {
  const type = normalizeLoveWinsAllReplayEventType(eventType);
  if (type === "init") {
    throw new Error("loveWinsAllEventToAction cannot convert the init event");
  }
  return {
    type: "Bet",
    playerId: asPlayerId(payload.playerId),
    move: asBetMove(payload.move),
  };
}
